import React, { useState, useRef } from 'react';
import { projectApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Plus, X, ShieldCheck, GripVertical, ChevronDown, ChevronRight,
  Pencil, ListChecks, User as UserIcon, Calendar, Camera, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const fullUrl = (u) => (u && u.startsWith('/') ? `${BACKEND}${u}` : u);

// Completion fraction: checked = 1, else sub-tasks ratio, else 0
export const taskFraction = (t) => {
  if (t.completed) return 1;
  const subs = t.subtasks || [];
  if (subs.length) return subs.filter((s) => s.completed).length / subs.length;
  return 0;
};
export const phaseProgress = (tasks = []) =>
  tasks.length ? Math.round((tasks.reduce((a, t) => a + taskFraction(t), 0) / tasks.length) * 100) : 0;

export const PhaseTasks = ({ projectId, phaseName, tasks = [], user, onUpdate, assignees = [] }) => {
  const canEdit = user?.role === 'owner' || user?.role === 'manager';
  const [addingTask, setAddingTask] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [expanded, setExpanded] = useState({});          // taskId -> bool
  const [editingName, setEditingName] = useState(null);  // taskId being renamed
  const [nameDraft, setNameDraft] = useState('');
  const [descDraft, setDescDraft] = useState({});        // taskId -> string (unsaved)
  const [newSubtask, setNewSubtask] = useState({});      // taskId -> string
  const [localTasks, setLocalTasks] = useState(tasks);
  const dragId = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);
  const fileInputs = useRef({});

  const todayStr = new Date().toISOString().slice(0, 10);
  const isOverdue = (t) => t.due_date && !t.completed && t.due_date < todayStr;

  // keep local list in sync when parent refetches
  React.useEffect(() => { setLocalTasks(tasks); }, [tasks]);

  const toggleExpand = (taskId) =>
    setExpanded((e) => ({ ...e, [taskId]: !e[taskId] }));

  const handleAddTask = async () => {
    if (!newTaskName.trim()) return;
    try {
      await projectApi.addTask(projectId, phaseName, { name: newTaskName.trim() });
      setNewTaskName('');
      setAddingTask(false);
      onUpdate();
      toast.success('Task added');
    } catch {
      toast.error('Failed to add task');
    }
  };

  const handleToggleTask = async (taskId, completed) => {
    try {
      await projectApi.updateTask(projectId, phaseName, taskId, { completed });
      onUpdate();
    } catch {
      toast.error('Failed to update task');
    }
  };

  const handleVerify = async (taskId, verified) => {
    try {
      await projectApi.verifyTask(projectId, phaseName, taskId, verified);
      onUpdate();
      toast.success(verified ? 'Task verified' : 'Verification removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to verify task');
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await projectApi.deleteTask(projectId, phaseName, taskId);
      onUpdate();
      toast.success('Task deleted');
    } catch {
      toast.error('Failed to delete task');
    }
  };

  const saveName = async (taskId) => {
    if (!nameDraft.trim()) { setEditingName(null); return; }
    try {
      await projectApi.updateTask(projectId, phaseName, taskId, { name: nameDraft.trim() });
      setEditingName(null);
      onUpdate();
    } catch {
      toast.error('Failed to rename task');
    }
  };

  const saveDescription = async (taskId) => {
    try {
      await projectApi.updateTask(projectId, phaseName, taskId, { description: descDraft[taskId] ?? '' });
      onUpdate();
      toast.success('Description saved');
    } catch {
      toast.error('Failed to save description');
    }
  };

  const setAssignee = async (task, value) => {
    try {
      if (value === '__none__') {
        await projectApi.updateTask(projectId, phaseName, task.id, { clear_assignee: true });
      } else {
        const a = assignees.find((x) => `${x.type}:${x.id}` === value);
        if (!a) return;
        await projectApi.updateTask(projectId, phaseName, task.id, {
          assigned_to: a.id, assigned_type: a.type, assignee_name: a.name,
        });
      }
      onUpdate();
    } catch {
      toast.error('Failed to assign task');
    }
  };

  const setDueDate = async (task, date) => {
    try {
      await projectApi.updateTask(projectId, phaseName, task.id, { due_date: date });
      onUpdate();
    } catch {
      toast.error('Failed to set due date');
    }
  };

  const uploadPhoto = async (task, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await projectApi.addTaskPhoto(projectId, phaseName, task.id, { image_data: reader.result });
        onUpdate();
        toast.success('Photo added');
      } catch {
        toast.error('Failed to upload photo');
      }
    };
    reader.readAsDataURL(file);
  };

  const deletePhoto = async (task, photoId) => {
    try {
      await projectApi.deleteTaskPhoto(projectId, phaseName, task.id, photoId);
      onUpdate();
    } catch {
      toast.error('Failed to delete photo');
    }
  };

  const updateSubtasks = async (task, nextSubtasks) => {
    // optimistic local update
    setLocalTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, subtasks: nextSubtasks } : t)));
    try {
      await projectApi.updateTask(projectId, phaseName, task.id, {
        subtasks: nextSubtasks.map((s) => ({ id: s.id, name: s.name, completed: !!s.completed })),
      });
      onUpdate();
    } catch {
      toast.error('Failed to update sub-tasks');
      onUpdate();
    }
  };

  const addSubtask = (task) => {
    const name = (newSubtask[task.id] || '').trim();
    if (!name) return;
    const tempId = (window.crypto?.randomUUID?.() || `tmp-${Date.now()}`);
    const next = [...(task.subtasks || []), { id: tempId, name, completed: false }];
    setNewSubtask((s) => ({ ...s, [task.id]: '' }));
    updateSubtasks(task, next);
  };

  const removeSubtask = (task, subId) =>
    updateSubtasks(task, (task.subtasks || []).filter((s) => s.id !== subId));

  const toggleSubtask = async (task, sub, completed) => {
    // Anyone can check off — dedicated endpoint
    setLocalTasks((prev) => prev.map((t) => t.id === task.id
      ? { ...t, subtasks: (t.subtasks || []).map((s) => s.id === sub.id ? { ...s, completed } : s) }
      : t));
    try {
      await projectApi.toggleSubtask(projectId, phaseName, task.id, sub.id, completed);
      onUpdate();
    } catch {
      toast.error('Failed to update sub-task');
      onUpdate();
    }
  };

  // ---- drag reorder (owners/managers only) ----
  const onDragStart = (taskId) => { dragId.current = taskId; };
  const onDragOver = (e, taskId) => { e.preventDefault(); if (taskId !== dragOverId) setDragOverId(taskId); };
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    const sourceId = dragId.current;
    dragId.current = null;
    setDragOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const ids = localTasks.map((t) => t.id);
    const from = ids.indexOf(sourceId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const next = [...localTasks];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setLocalTasks(next);
    try {
      await projectApi.reorderTasks(projectId, phaseName, next.map((t) => t.id));
      onUpdate();
    } catch {
      toast.error('Failed to reorder tasks');
      onUpdate();
    }
  };

  return (
    <div className="mt-3 ml-1 sm:ml-6 space-y-1.5" data-testid={`phase-tasks-${phaseName.toLowerCase().replace(/\s+/g, '-')}`}>
      {localTasks.length > 0 && (
        <div className="flex items-center gap-2 mb-1">
          <Progress value={phaseProgress(localTasks)} className="h-1.5 flex-1" />
          <span className="text-[11px] text-muted-foreground shrink-0" data-testid={`phase-progress-${phaseName.toLowerCase().replace(/\s+/g, '-')}`}>
            {phaseProgress(localTasks)}%
          </span>
        </div>
      )}
      {localTasks.map((task) => {
        const subs = task.subtasks || [];
        const doneSubs = subs.filter((s) => s.completed).length;
        const isOpen = !!expanded[task.id];
        return (
          <div
            key={task.id}
            draggable={canEdit}
            onDragStart={() => onDragStart(task.id)}
            onDragOver={(e) => onDragOver(e, task.id)}
            onDrop={(e) => onDrop(e, task.id)}
            className={`rounded-sm border ${dragOverId === task.id ? 'border-orange-400 bg-orange-50/50' : 'border-transparent'}`}
            data-testid={`task-item-${task.id}`}
          >
            <div className="flex items-start gap-2 text-sm py-1 px-1">
              {canEdit && (
                <button
                  className="cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground mt-0.5"
                  title="Drag to reorder"
                  data-testid={`task-drag-${task.id}`}
                  onClick={(e) => e.preventDefault()}
                >
                  <GripVertical className="h-4 w-4" />
                </button>
              )}

              {/* Completion */}
              <div className="flex flex-col items-center gap-0.5 min-w-[20px]">
                <Checkbox
                  checked={task.completed}
                  onCheckedChange={(checked) => handleToggleTask(task.id, checked)}
                  className="h-4 w-4 mt-0.5"
                  data-testid={`task-checkbox-${task.id}`}
                />
                {task.completed && task.completed_by_name && (
                  <span className="text-[10px] text-muted-foreground truncate max-w-[60px]" title={task.completed_by_name}>
                    {task.completed_by_name.split(' ')[0]}
                  </span>
                )}
              </div>

              {/* Verify */}
              {task.completed && (user?.role === 'owner' || user?.role === 'manager') && (
                <button
                  onClick={() => handleVerify(task.id, !task.verified)}
                  className={`h-4 w-4 mt-0.5 rounded flex items-center justify-center transition-colors ${task.verified ? 'bg-green-600 text-white' : 'bg-muted hover:bg-muted/80 text-muted-foreground'}`}
                  title={task.verified ? `Verified by ${task.verified_by_name}` : 'Click to verify'}
                >
                  <ShieldCheck className="h-3 w-3" />
                </button>
              )}
              {task.completed && task.verified && user?.role === 'foreperson' && (
                <div className="h-4 w-4 mt-0.5 rounded bg-green-600 text-white flex items-center justify-center" title={`Verified by ${task.verified_by_name}`}>
                  <ShieldCheck className="h-3 w-3" />
                </div>
              )}

              {/* Name (inline editable) */}
              <div className="flex-1 min-w-0">
                {editingName === task.id ? (
                  <div className="flex gap-2">
                    <Input
                      autoFocus
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveName(task.id); if (e.key === 'Escape') setEditingName(null); }}
                      className="h-7 text-sm rounded-sm"
                      data-testid={`task-name-input-${task.id}`}
                    />
                    <Button size="sm" className="h-7 rounded-sm" onClick={() => saveName(task.id)} data-testid={`task-name-save-${task.id}`}>Save</Button>
                  </div>
                ) : (
                  <button
                    className="flex items-center gap-1.5 text-left w-full group"
                    onClick={() => toggleExpand(task.id)}
                    data-testid={`task-expand-${task.id}`}
                  >
                    {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                    <span className={task.completed ? 'line-through text-muted-foreground' : ''}>{task.name}</span>
                    {task.description ? <Pencil className="h-3 w-3 text-muted-foreground/60 shrink-0" title="Has description" /> : null}
                    {subs.length > 0 && (
                      <span className="text-[11px] text-muted-foreground flex items-center gap-0.5 shrink-0">
                        <ListChecks className="h-3 w-3" />{doneSubs}/{subs.length}
                      </span>
                    )}
                    {task.added_by_gc && (
                      <Badge variant="secondary" className="rounded-sm text-[10px] px-1 py-0 shrink-0">GC</Badge>
                    )}
                  </button>
                )}
                {/* meta row: assignee + due date */}
                {(task.assignee_name || task.due_date || (task.gc_notes || []).length > 0) && editingName !== task.id && (
                  <div className="flex flex-wrap items-center gap-2 mt-0.5 ml-5">
                    {task.assignee_name && (
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <UserIcon className="h-3 w-3" />{task.assignee_name}
                      </span>
                    )}
                    {task.due_date && (
                      <span className={`text-[11px] flex items-center gap-1 ${isOverdue(task) ? 'text-red-600 font-medium' : 'text-muted-foreground'}`} data-testid={`task-due-${task.id}`}>
                        <Calendar className="h-3 w-3" />{task.due_date}{isOverdue(task) ? ' • Overdue' : ''}
                      </span>
                    )}
                    {(task.gc_notes || []).length > 0 && (
                      <span className="text-[11px] text-amber-600 flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" />{(task.gc_notes || []).length} GC note{(task.gc_notes || []).length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Row actions */}
              {canEdit && editingName !== task.id && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-50 hover:opacity-100"
                    onClick={() => { setEditingName(task.id); setNameDraft(task.name); }}
                    title="Rename" data-testid={`task-edit-${task.id}`}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-50 hover:opacity-100"
                    onClick={() => handleDelete(task.id)} title="Delete" data-testid={`task-delete-${task.id}`}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>

            {/* Expanded panel: description + subtasks */}
            {isOpen && (
              <div className="ml-7 mr-1 mb-2 mt-1 space-y-3 border-l-2 border-muted pl-3" data-testid={`task-panel-${task.id}`}>
                {/* Assignment + due date */}
                {canEdit && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">Assigned to</p>
                      <Select value={task.assigned_to ? `${task.assigned_type}:${task.assigned_to}` : '__none__'} onValueChange={(v) => setAssignee(task, v)}>
                        <SelectTrigger className="h-8 rounded-sm text-sm" data-testid={`task-assign-${task.id}`}>
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Unassigned</SelectItem>
                          {assignees.map((a) => (
                            <SelectItem key={`${a.type}:${a.id}`} value={`${a.type}:${a.id}`}>
                              {a.name}{a.type === 'subcontractor' ? ' (Sub)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">Due date</p>
                      <Input type="date" value={task.due_date || ''} onChange={(e) => setDueDate(task, e.target.value)}
                        className="h-8 rounded-sm text-sm" data-testid={`task-duedate-${task.id}`} />
                    </div>
                  </div>
                )}

                {/* Proof-of-completion photos */}
                <div>
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">Proof photos</p>
                  <div className="flex flex-wrap gap-2">
                    {(task.photos || []).map((ph) => (
                      <div key={ph.id} className="relative group" data-testid={`task-photo-${ph.id}`}>
                        <img src={fullUrl(ph.image_url)} alt="proof" className="h-16 w-16 object-cover rounded-sm border" />
                        {canEdit && (
                          <button onClick={() => deletePhoto(task, ph.id)}
                            className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full h-4 w-4 flex items-center justify-center opacity-0 group-hover:opacity-100"
                            data-testid={`task-photo-delete-${ph.id}`}>
                            <X className="h-2.5 w-2.5" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => fileInputs.current[task.id]?.click()}
                      className="h-16 w-16 rounded-sm border border-dashed flex flex-col items-center justify-center text-muted-foreground hover:border-orange-400 hover:text-orange-500"
                      data-testid={`task-photo-add-${task.id}`}>
                      <Camera className="h-4 w-4" />
                      <span className="text-[9px] mt-0.5">Add</span>
                    </button>
                    <input
                      ref={(el) => { fileInputs.current[task.id] = el; }}
                      type="file" accept="image/*" className="hidden"
                      onChange={(e) => { uploadPhoto(task, e.target.files?.[0]); e.target.value = ''; }}
                    />
                  </div>
                </div>

                {/* GC notes (read-only internally) */}
                {(task.gc_notes || []).length > 0 && (
                  <div>
                    <p className="text-[11px] font-medium text-amber-600 uppercase tracking-wide mb-1">General Contractor notes</p>
                    <div className="space-y-1">
                      {(task.gc_notes || []).map((n) => (
                        <div key={n.id} className="text-sm bg-amber-50 border border-amber-200 rounded-sm px-2 py-1" data-testid={`gc-note-${n.id}`}>
                          {n.note}
                          <span className="block text-[10px] text-amber-700/70">{(n.created_at || '').slice(0, 10)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                <div>
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">Description</p>
                  {canEdit ? (
                    <div className="space-y-1">
                      <Textarea
                        rows={2}
                        placeholder="Add details, specs, instructions…"
                        value={descDraft[task.id] ?? task.description ?? ''}
                        onChange={(e) => setDescDraft((d) => ({ ...d, [task.id]: e.target.value }))}
                        className="text-sm rounded-sm"
                        data-testid={`task-desc-input-${task.id}`}
                      />
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs"
                        onClick={() => saveDescription(task.id)} data-testid={`task-desc-save-${task.id}`}>
                        Save description
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{task.description || 'No description'}</p>
                  )}
                </div>

                {/* Sub-tasks */}
                <div>
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">Sub-tasks</p>
                  <div className="space-y-1">
                    {subs.map((sub) => (
                      <div key={sub.id} className="flex items-center gap-2 text-sm" data-testid={`subtask-${sub.id}`}>
                        <Checkbox
                          checked={sub.completed}
                          onCheckedChange={(checked) => toggleSubtask(task, sub, checked)}
                          className="h-3.5 w-3.5"
                          data-testid={`subtask-checkbox-${sub.id}`}
                        />
                        <span className={`flex-1 ${sub.completed ? 'line-through text-muted-foreground' : ''}`}>{sub.name}</span>
                        {canEdit && (
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0 opacity-50 hover:opacity-100"
                            onClick={() => removeSubtask(task, sub.id)} data-testid={`subtask-delete-${sub.id}`}>
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {subs.length === 0 && !canEdit && (
                      <p className="text-xs text-muted-foreground">No sub-tasks</p>
                    )}
                  </div>
                  {canEdit && (
                    <div className="flex gap-2 mt-2">
                      <Input
                        placeholder="Add a sub-task"
                        value={newSubtask[task.id] || ''}
                        onChange={(e) => setNewSubtask((s) => ({ ...s, [task.id]: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && addSubtask(task)}
                        className="h-7 text-sm rounded-sm"
                        data-testid={`subtask-input-${task.id}`}
                      />
                      <Button size="sm" className="h-7 rounded-sm" onClick={() => addSubtask(task)} data-testid={`subtask-add-${task.id}`}>
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Add task */}
      {canEdit && (
        addingTask ? (
          <div className="flex gap-2 mt-2">
            <Input
              autoFocus
              placeholder="Task name"
              value={newTaskName}
              onChange={(e) => setNewTaskName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddTask(); if (e.key === 'Escape') { setAddingTask(false); setNewTaskName(''); } }}
              className="h-8 text-sm rounded-sm"
              data-testid={`add-task-input-${phaseName.toLowerCase().replace(/\s+/g, '-')}`}
            />
            <Button size="sm" className="h-8 rounded-sm" onClick={handleAddTask}>Add</Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => { setAddingTask(false); setNewTaskName(''); }}>Cancel</Button>
          </div>
        ) : (
          <Button variant="ghost" size="sm" className="mt-1 text-xs"
            onClick={() => setAddingTask(true)}
            data-testid={`add-task-btn-${phaseName.toLowerCase().replace(/\s+/g, '-')}`}>
            <Plus className="h-3 w-3 mr-1" />
            Add Task
          </Button>
        )
      )}
    </div>
  );
};

export default PhaseTasks;
